import { useCallback, useEffect } from 'react'

import { noOp } from '@/utils/helpers'
import logger from '@/utils/logger'

import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import { useStateRef } from '@/client/hooks/useStateRef'

import {
  type AssemblyAIState,
  assemblyAITransitionToState,
} from '@/components/Conversation/hooks/useSpeechRecognition/assemblyAIProvider'
import {
  type WebSpeechState,
  webSpeechTransitionToState,
} from '@/components/Conversation/hooks/useSpeechRecognition/webSpeechProvider'

import { ProviderType, type SpeechRecognitionAction, type UseVoiceRecognitionParams } from './types'

// Union type for all provider states
type ProviderState = WebSpeechState | AssemblyAIState

// One-time check for WebSpeech support - result won't change during app lifetime
// TODO: Uncomment this once AssemblyAI is supported
// const isWebSpeechSupported = (() => {
//   const recognition =
//     initializeGenericSpeechRecognition(globalThis.webkitSpeechRecognition) ||
//     initializeGenericSpeechRecognition(globalThis.SpeechRecognition)
//   return recognition != null
// })()

// For now, always try WebSpeech
const isWebSpeechSupported = true

// Determine provider type once at module level
const providerType: ProviderType = isWebSpeechSupported
  ? ProviderType.WebSpeech
  : ProviderType.AssemblyAI

const useSpeechRecognition = ({ onTranscriptFragment }: UseVoiceRecognitionParams) => {
  // Combined state managed by useStateRef
  const [speechRecognitionState, setSpeechRecognitionState, speechRecognitionStateRef] =
    useStateRef<ProviderState>({
      type: 'idle',
      provider: providerType,
    })

  // Helper to handle async errors from providers (i.e. an error inside WS onMessage)
  const onError = useCallback(
    (error: string) => {
      setSpeechRecognitionState({
        type: 'error',
        provider: providerType,
        error,
      })

      logger.error('Speech recognition error', { error, providerType })
    },
    [setSpeechRecognitionState]
  )

  // Call the provider's transition function, and handle errors
  const [updateProviderState, isUpdatingProviderState] = useAPIWrapper(
    async (action: SpeechRecognitionAction) => {
      let newState: ProviderState

      if (speechRecognitionStateRef.current.provider === ProviderType.WebSpeech) {
        newState = webSpeechTransitionToState(speechRecognitionStateRef.current, action, onError)
      } else if (speechRecognitionStateRef.current.provider === ProviderType.AssemblyAI) {
        newState = await assemblyAITransitionToState(
          speechRecognitionStateRef.current,
          action,
          onError
        )
      } else {
        onError('Invalid provider state')
        return
      }

      // Handle error states
      if (newState.type === 'error') {
        onError(newState.error)
      } else {
        setSpeechRecognitionState(newState)
      }
    },
    [onError, setSpeechRecognitionState, speechRecognitionStateRef],
    'Update Speech Recognition Provider State'
  )

  // Start recording -- if idle, start session first, otherwise start recording
  const startRecording = useCallback(async () => {
    if (speechRecognitionStateRef.current.type === 'recording') {
      return
    }

    if (speechRecognitionStateRef.current.type === 'idle') {
      await updateProviderState({
        type: 'start-session',
      })
    }

    if (speechRecognitionStateRef.current.type === 'session-ready') {
      await updateProviderState({
        type: 'start-recording',
        onTranscriptFragment,
      })
    }
  }, [updateProviderState, onTranscriptFragment, speechRecognitionStateRef])

  // Stop recording -- if not recording, do nothing
  const stopRecording = useCallback(async () => {
    if (speechRecognitionStateRef.current.type !== 'recording') {
      return
    }

    await updateProviderState({ type: 'stop-recording' })
  }, [updateProviderState, speechRecognitionStateRef])

  // Cleanup on unmount -- reset the provider state (destroys any resources)
  useEffect(
    () => () => {
      updateProviderState({ type: 'reset' }).catch(noOp)
    },
    [updateProviderState]
  )

  return {
    isRecording: speechRecognitionState.type === 'recording',
    startRecording,
    stopRecording,
    error: 'error' in speechRecognitionState ? speechRecognitionState.error : null,
    isUpdatingProviderState,
  }
}

export default useSpeechRecognition
